import React, { useState, useMemo, useCallback } from 'react';
import ReactFlow, {
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  MarkerType,
  Panel,
  Handle,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { ArrowLeft, Zap, X, ChevronRight, Code, Database, Settings, Cpu, FileOutput } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  INPUT_VARIABLES,
  PROCESSING_FUNCTIONS,
  OUTPUT_SECTIONS,
  MODE_COMPARISON,
  DEPENDENCIES,
  FINAL_OUTPUT,
} from '../../data/brainArchitecture';

// ============================================================================
// CUSTOM NODE COMPONENTS
// ============================================================================

// Input Variable Node - with handles for edges
const InputNode = ({ data, selected }) => (
  <div
    style={{
      ...nodeStyles.base,
      ...nodeStyles.input,
      borderColor: selected ? '#22c55e' : '#22c55e40',
      boxShadow: selected ? '0 0 30px #22c55e60' : '0 0 15px #22c55e20',
    }}
  >
    <Handle type="source" position={Position.Bottom} style={handleStyles.source} />
    <div style={nodeStyles.category}>INPUT</div>
    <div style={nodeStyles.icon}>📥</div>
    <div style={nodeStyles.name}>{data.name}</div>
    <div style={nodeStyles.type}>{data.type}</div>
  </div>
);

// Processing Function Node - with handles for edges
const ProcessNode = ({ data, selected }) => (
  <div
    style={{
      ...nodeStyles.base,
      ...nodeStyles.process,
      borderColor: selected ? '#3b82f6' : '#3b82f640',
      boxShadow: selected ? '0 0 30px #3b82f660' : '0 0 15px #3b82f620',
    }}
  >
    <Handle type="target" position={Position.Top} style={handleStyles.target} />
    <Handle type="source" position={Position.Bottom} style={handleStyles.source} />
    <div style={nodeStyles.category}>PROCESS</div>
    <div style={nodeStyles.icon}>{data.icon}</div>
    <div style={nodeStyles.name}>{data.name}</div>
  </div>
);

// Output Section Node - with handles for edges
const OutputNode = ({ data, selected }) => (
  <div
    style={{
      ...nodeStyles.base,
      ...nodeStyles.output,
      borderColor: selected ? '#f59e0b' : '#f59e0b40',
      boxShadow: selected ? '0 0 30px #f59e0b60' : '0 0 15px #f59e0b20',
    }}
  >
    <Handle type="target" position={Position.Top} style={handleStyles.target} />
    <Handle type="source" position={Position.Bottom} style={handleStyles.source} />
    <div style={nodeStyles.category}>OUTPUT</div>
    <div style={nodeStyles.icon}>{data.icon}</div>
    <div style={nodeStyles.name}>{data.name}</div>
  </div>
);

// Final Output Node - with handles for edges
const FinalNode = ({ data, selected }) => (
  <div
    style={{
      ...nodeStyles.base,
      ...nodeStyles.final,
      borderColor: selected ? '#ec4899' : '#ec489940',
      boxShadow: selected ? '0 0 30px #ec489960' : '0 0 15px #ec489920',
    }}
  >
    <Handle type="target" position={Position.Top} style={handleStyles.target} />
    <div style={nodeStyles.category}>TO LLM</div>
    <div style={nodeStyles.icon}>🤖</div>
    <div style={nodeStyles.name}>{data.name}</div>
  </div>
);

const handleStyles = {
  source: {
    background: '#6b7280',
    width: '8px',
    height: '8px',
    border: 'none',
  },
  target: {
    background: '#6b7280',
    width: '8px',
    height: '8px',
    border: 'none',
  },
};

const nodeStyles = {
  base: {
    padding: '12px 16px',
    borderRadius: '12px',
    border: '2px solid',
    backgroundColor: '#111827',
    minWidth: '140px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  input: { borderColor: '#22c55e40' },
  process: { borderColor: '#3b82f640' },
  output: { borderColor: '#f59e0b40' },
  final: { borderColor: '#ec489940' },
  category: {
    fontSize: '9px',
    fontWeight: 700,
    letterSpacing: '1px',
    color: '#6b7280',
    marginBottom: '4px',
  },
  icon: {
    fontSize: '24px',
    marginBottom: '4px',
  },
  name: {
    color: 'white',
    fontSize: '12px',
    fontWeight: 500,
  },
  type: {
    color: '#6b7280',
    fontSize: '10px',
    marginTop: '2px',
  },
};

const nodeTypes = {
  input: InputNode,
  process: ProcessNode,
  output: OutputNode,
  final: FinalNode,
};

// ============================================================================
// DETAIL PANEL COMPONENT
// ============================================================================

const DetailPanel = ({ item, type, onClose }) => {
  if (!item) return null;

  const renderContent = () => {
    if (type === 'input') {
      return (
        <>
          <div style={detailStyles.field}>
            <span style={detailStyles.label}>Type:</span>
            <code style={detailStyles.code}>{item.type}</code>
          </div>
          {item.options && (
            <div style={detailStyles.field}>
              <span style={detailStyles.label}>Options:</span>
              <div style={detailStyles.options}>
                {item.options.map(opt => (
                  <span key={opt} style={detailStyles.option}>{opt}</span>
                ))}
              </div>
            </div>
          )}
          {item.default !== undefined && (
            <div style={detailStyles.field}>
              <span style={detailStyles.label}>Default:</span>
              <code style={detailStyles.code}>{String(item.default)}</code>
            </div>
          )}
          <div style={detailStyles.field}>
            <span style={detailStyles.label}>Description:</span>
            <p style={detailStyles.desc}>{item.description}</p>
          </div>
          {item.example && (
            <div style={detailStyles.field}>
              <span style={detailStyles.label}>Example:</span>
              <code style={detailStyles.code}>{item.example}</code>
            </div>
          )}
          <div style={detailStyles.field}>
            <span style={detailStyles.label}>Flows to:</span>
            <div style={detailStyles.flowList}>
              {item.flowsTo?.map(f => (
                <span key={f} style={detailStyles.flowItem}>{f}</span>
              ))}
            </div>
          </div>
        </>
      );
    }

    if (type === 'process') {
      return (
        <>
          <div style={detailStyles.field}>
            <span style={detailStyles.label}>Description:</span>
            <p style={detailStyles.desc}>{item.description}</p>
          </div>
          <div style={detailStyles.field}>
            <span style={detailStyles.label}>Inputs:</span>
            <div style={detailStyles.flowList}>
              {item.inputs?.map(f => (
                <span key={f} style={{ ...detailStyles.flowItem, backgroundColor: '#22c55e20', color: '#22c55e' }}>{f}</span>
              ))}
            </div>
          </div>
          <div style={detailStyles.field}>
            <span style={detailStyles.label}>Outputs:</span>
            <div style={detailStyles.flowList}>
              {item.outputs?.map(f => (
                <span key={f} style={{ ...detailStyles.flowItem, backgroundColor: '#f59e0b20', color: '#f59e0b' }}>{f}</span>
              ))}
            </div>
          </div>
          <div style={detailStyles.field}>
            <span style={detailStyles.label}>Calculation:</span>
            <pre style={detailStyles.calcBlock}>{item.calculation}</pre>
          </div>
          {item.code && (
            <div style={detailStyles.field}>
              <span style={detailStyles.label}>Code:</span>
              <pre style={detailStyles.codeBlock}>{item.code}</pre>
            </div>
          )}
        </>
      );
    }

    if (type === 'output') {
      return (
        <>
          <div style={detailStyles.field}>
            <span style={detailStyles.label}>Description:</span>
            <p style={detailStyles.desc}>{item.description}</p>
          </div>
          <div style={detailStyles.field}>
            <span style={detailStyles.label}>Built from:</span>
            <div style={detailStyles.flowList}>
              {item.builtFrom?.map(f => (
                <span key={f} style={detailStyles.flowItem}>{f}</span>
              ))}
            </div>
          </div>
          <div style={detailStyles.field}>
            <span style={detailStyles.label}>Content:</span>
            <pre style={detailStyles.contentBlock}>{item.content}</pre>
          </div>
        </>
      );
    }

    return null;
  };

  return (
    <div style={detailStyles.panel}>
      <div style={detailStyles.header}>
        <div>
          <span style={detailStyles.headerType}>{type.toUpperCase()}</span>
          <h3 style={detailStyles.headerTitle}>{item.icon} {item.name}</h3>
        </div>
        <button onClick={onClose} style={detailStyles.closeBtn}>
          <X size={20} />
        </button>
      </div>
      <div style={detailStyles.body}>
        {renderContent()}
      </div>
    </div>
  );
};

const detailStyles = {
  panel: {
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    width: '400px',
    backgroundColor: '#0f172a',
    borderLeft: '1px solid #1e293b',
    zIndex: 100,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    padding: '20px',
    borderBottom: '1px solid #1e293b',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  headerType: {
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '1px',
    color: '#6b7280',
  },
  headerTitle: {
    color: 'white',
    fontSize: '18px',
    fontWeight: 600,
    margin: '4px 0 0 0',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    color: '#6b7280',
    cursor: 'pointer',
    padding: '4px',
  },
  body: {
    padding: '20px',
    overflowY: 'auto',
    flex: 1,
  },
  field: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.5px',
    color: '#9ca3af',
    marginBottom: '6px',
    textTransform: 'uppercase',
  },
  code: {
    backgroundColor: '#1e293b',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '13px',
    color: '#22c55e',
    fontFamily: 'monospace',
  },
  desc: {
    color: '#d1d5db',
    fontSize: '14px',
    lineHeight: 1.6,
    margin: 0,
  },
  options: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
  },
  option: {
    backgroundColor: '#1e293b',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '11px',
    color: '#9ca3af',
  },
  flowList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
  },
  flowItem: {
    backgroundColor: '#3b82f620',
    color: '#3b82f6',
    padding: '4px 10px',
    borderRadius: '100px',
    fontSize: '11px',
    fontWeight: 500,
  },
  calcBlock: {
    backgroundColor: '#1e293b',
    padding: '12px',
    borderRadius: '8px',
    fontSize: '12px',
    color: '#d1d5db',
    lineHeight: 1.6,
    whiteSpace: 'pre-wrap',
    margin: 0,
    fontFamily: 'inherit',
  },
  codeBlock: {
    backgroundColor: '#0d1117',
    padding: '12px',
    borderRadius: '8px',
    fontSize: '11px',
    color: '#c9d1d9',
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap',
    margin: 0,
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    maxHeight: '300px',
    overflowY: 'auto',
  },
  contentBlock: {
    backgroundColor: '#1e293b',
    padding: '12px',
    borderRadius: '8px',
    fontSize: '12px',
    color: '#f59e0b',
    lineHeight: 1.6,
    whiteSpace: 'pre-wrap',
    margin: 0,
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  },
};

// ============================================================================
// MODE PANEL COMPONENT
// ============================================================================

const ModePanel = ({ onClose }) => (
  <div style={detailStyles.panel}>
    <div style={detailStyles.header}>
      <div>
        <span style={detailStyles.headerType}>MODES</span>
        <h3 style={detailStyles.headerTitle}>🔀 Mode Comparison</h3>
      </div>
      <button onClick={onClose} style={detailStyles.closeBtn}>
        <X size={20} />
      </button>
    </div>
    <div style={detailStyles.body}>
      {Object.entries(MODE_COMPARISON).map(([key, mode]) => (
        <div key={key} style={modeStyles.modeCard}>
          <div style={modeStyles.modeName}>{mode.name}</div>
          <p style={modeStyles.modeDesc}>{mode.description}</p>
          <div style={modeStyles.modeFields}>
            <div><span style={modeStyles.fieldLabel}>Brackets:</span> {mode.brackets}</div>
            <div><span style={modeStyles.fieldLabel}>Tone:</span> {mode.tone}</div>
            <div><span style={modeStyles.fieldLabel}>Context:</span> {mode.context}</div>
            <div><span style={modeStyles.fieldLabel}>Used by:</span> {mode.usedBy}</div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const modeStyles = {
  modeCard: {
    backgroundColor: '#1e293b',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '12px',
  },
  modeName: {
    color: 'white',
    fontWeight: 600,
    marginBottom: '4px',
  },
  modeDesc: {
    color: '#9ca3af',
    fontSize: '13px',
    margin: '0 0 12px 0',
  },
  modeFields: {
    fontSize: '12px',
    color: '#d1d5db',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  fieldLabel: {
    color: '#6b7280',
    marginRight: '4px',
  },
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const ArchitectureExplorer = () => {
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [showModes, setShowModes] = useState(false);

  // Build nodes for React Flow - better spacing for visibility
  const initialNodes = useMemo(() => {
    const nodes = [];

    // === ROW 1: INPUT VARIABLES (grouped by category) ===
    // Core inputs (most important - review text)
    const coreInputs = INPUT_VARIABLES.filter(v => v.category === 'core');
    coreInputs.forEach((input, i) => {
      nodes.push({
        id: input.id,
        type: 'input',
        position: { x: 600, y: 50 }, // Center top
        data: { ...input, onClick: () => { setSelectedItem(input); setSelectedType('input'); } },
      });
    });

    // Context inputs (left side)
    const contextInputs = INPUT_VARIABLES.filter(v => v.category === 'context');
    contextInputs.forEach((input, i) => {
      nodes.push({
        id: input.id,
        type: 'input',
        position: { x: 50 + i * 170, y: 150 },
        data: { ...input, onClick: () => { setSelectedItem(input); setSelectedType('input'); } },
      });
    });

    // Style inputs (right side)
    const styleInputs = INPUT_VARIABLES.filter(v => v.category === 'style');
    styleInputs.forEach((input, i) => {
      nodes.push({
        id: input.id,
        type: 'input',
        position: { x: 750 + i * 170, y: 150 },
        data: { ...input, onClick: () => { setSelectedItem(input); setSelectedType('input'); } },
      });
    });

    // System inputs (far right)
    const systemInputs = INPUT_VARIABLES.filter(v => v.category === 'system');
    systemInputs.forEach((input, i) => {
      nodes.push({
        id: input.id,
        type: 'input',
        position: { x: 1450, y: 100 },
        data: { ...input, onClick: () => { setSelectedItem(input); setSelectedType('input'); } },
      });
    });

    // === ROW 2: PROCESSING FUNCTIONS (spread out) ===
    const processY = 400;
    const processingPositions = {
      detectLanguage: { x: 200, y: processY },
      analyzeSentiment: { x: 400, y: processY },
      analyzeAmbiguity: { x: 600, y: processY },
      extractEntities: { x: 800, y: processY },
      calculateContextScore: { x: 200, y: processY + 150 },
      calculateBracketLevel: { x: 600, y: processY + 150 },
      calculateResponseLength: { x: 1000, y: processY },
      shouldIncludeEmojis: { x: 1000, y: processY + 150 },
      getUnifiedContext: { x: 50, y: processY + 75 },
      modeRouter: { x: 1200, y: processY + 75 },
    };

    PROCESSING_FUNCTIONS.forEach((proc) => {
      const pos = processingPositions[proc.id] || { x: 600, y: processY };
      nodes.push({
        id: proc.id,
        type: 'process',
        position: pos,
        data: { ...proc, onClick: () => { setSelectedItem(proc); setSelectedType('process'); } },
      });
    });

    // === ROW 3: OUTPUT SECTIONS ===
    const outputY = 750;
    OUTPUT_SECTIONS.forEach((output, i) => {
      nodes.push({
        id: output.id,
        type: 'output',
        position: { x: 150 + i * 220, y: outputY },
        data: { ...output, onClick: () => { setSelectedItem(output); setSelectedType('output'); } },
      });
    });

    // === ROW 4: FINAL OUTPUT TO LLM ===
    const finalY = 950;
    nodes.push({
      id: 'systemMessage',
      type: 'final',
      position: { x: 400, y: finalY },
      data: { name: 'System Message', icon: '📝', ...FINAL_OUTPUT.systemMessage },
    });
    nodes.push({
      id: 'userMessage',
      type: 'final',
      position: { x: 700, y: finalY },
      data: { name: 'User Message', icon: '💬', ...FINAL_OUTPUT.userMessage },
    });
    nodes.push({
      id: 'maxTokens',
      type: 'final',
      position: { x: 1000, y: finalY },
      data: { name: 'Max Tokens', icon: '🔢', ...FINAL_OUTPUT.maxTokens },
    });

    return nodes;
  }, []);

  // Build edges - only create edges for nodes that exist
  const initialEdges = useMemo(() => {
    const edges = [];
    const edgeColors = {
      input: '#22c55e',
      process: '#3b82f6',
      output: '#f59e0b',
    };

    // Get all valid node IDs
    const validNodeIds = new Set([
      ...INPUT_VARIABLES.map(v => v.id),
      ...PROCESSING_FUNCTIONS.map(f => f.id),
      ...OUTPUT_SECTIONS.map(s => s.id),
      'systemMessage', 'userMessage', 'maxTokens',
    ]);

    // Only add edges where BOTH source and target exist
    DEPENDENCIES.forEach((dep, i) => {
      if (validNodeIds.has(dep.from) && validNodeIds.has(dep.to)) {
        edges.push({
          id: `edge-${dep.from}-${dep.to}`,
          source: dep.from,
          target: dep.to,
          type: 'smoothstep',
          animated: true,
          style: { stroke: edgeColors[dep.type] || '#6b7280', strokeWidth: 2, opacity: 0.7 },
          markerEnd: { type: MarkerType.ArrowClosed, color: edgeColors[dep.type] || '#6b7280', width: 15, height: 15 },
        });
      }
    });

    // Connect outputs to final (systemMessage)
    OUTPUT_SECTIONS.forEach(output => {
      edges.push({
        id: `edge-${output.id}-final`,
        source: output.id,
        target: 'systemMessage',
        type: 'smoothstep',
        animated: true,
        style: { stroke: '#ec4899', strokeWidth: 2, opacity: 0.7 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#ec4899', width: 15, height: 15 },
      });
    });

    return edges;
  }, []);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  const onNodeClick = useCallback((event, node) => {
    if (node.data.onClick) {
      node.data.onClick();
    }
  }, []);

  return (
    <div style={styles.page}>
      {/* Custom styles */}
      <style>{`
        .react-flow__node {
          cursor: pointer !important;
        }
        .react-flow__node:hover {
          transform: scale(1.05);
        }
        .react-flow__controls {
          background: #1f2937 !important;
          border: 1px solid #374151 !important;
          border-radius: 8px !important;
        }
        .react-flow__controls-button {
          background: #1f2937 !important;
          border-bottom: 1px solid #374151 !important;
          fill: #9ca3af !important;
        }
      `}</style>

      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.headerLeft}>
            <Link to="/brain" style={styles.backLink}>
              <ArrowLeft size={20} />
              <span>Neurons</span>
            </Link>
            <div style={styles.divider} />
            <div style={styles.logoSection}>
              <Zap color="#facc15" size={20} />
              <span style={{ fontWeight: 600 }}>Architecture</span>
            </div>
          </div>
          <button onClick={() => setShowModes(true)} style={styles.modesBtn}>
            <Settings size={16} />
            Modes
          </button>
        </div>
      </header>

      {/* Legend */}
      <div style={styles.legend}>
        <div style={styles.legendItem}>
          <div style={{ ...styles.legendDot, backgroundColor: '#22c55e' }} />
          <span>Inputs</span>
        </div>
        <div style={styles.legendItem}>
          <div style={{ ...styles.legendDot, backgroundColor: '#3b82f6' }} />
          <span>Processing</span>
        </div>
        <div style={styles.legendItem}>
          <div style={{ ...styles.legendDot, backgroundColor: '#f59e0b' }} />
          <span>Output Sections</span>
        </div>
        <div style={styles.legendItem}>
          <div style={{ ...styles.legendDot, backgroundColor: '#ec4899' }} />
          <span>To LLM</span>
        </div>
      </div>

      {/* React Flow Canvas */}
      <div style={styles.canvas}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={onNodeClick}
          nodeTypes={nodeTypes}
          fitView
          minZoom={0.3}
          maxZoom={1.5}
          defaultViewport={{ x: 0, y: 0, zoom: 0.6 }}
          proOptions={{ hideAttribution: true }}
        >
          <Background color="#374151" gap={20} />
          <Controls showInteractive={false} />
        </ReactFlow>
      </div>

      {/* Detail Panel */}
      {selectedItem && (
        <DetailPanel
          item={selectedItem}
          type={selectedType}
          onClose={() => { setSelectedItem(null); setSelectedType(null); }}
        />
      )}

      {/* Mode Panel */}
      {showModes && <ModePanel onClose={() => setShowModes(false)} />}
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#030712',
    color: 'white',
    position: 'relative',
  },
  header: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 40,
    backgroundColor: 'rgba(17, 24, 39, 0.95)',
    backdropFilter: 'blur(12px)',
    borderBottom: '1px solid #1f2937',
  },
  headerContent: {
    maxWidth: '100%',
    padding: '12px 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  backLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#9ca3af',
    textDecoration: 'none',
    fontSize: '14px',
  },
  divider: {
    height: '24px',
    width: '1px',
    backgroundColor: '#374151',
  },
  logoSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  modesBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    backgroundColor: '#1f2937',
    border: '1px solid #374151',
    borderRadius: '8px',
    color: 'white',
    fontSize: '14px',
    cursor: 'pointer',
  },
  legend: {
    position: 'fixed',
    top: '70px',
    left: '24px',
    zIndex: 30,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    backgroundColor: 'rgba(17, 24, 39, 0.9)',
    padding: '12px 16px',
    borderRadius: '8px',
    border: '1px solid #374151',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '12px',
    color: '#9ca3af',
  },
  legendDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
  },
  canvas: {
    height: '100vh',
    paddingTop: '56px',
  },
};

export default ArchitectureExplorer;
